import { useState, useCallback, useEffect, useRef } from "react";
import {
  createFileRoute,
  useRouterState,
  useNavigate,
} from "@tanstack/react-router";
import { ArrowLineUp } from "@phosphor-icons/react";
import { Button, useToast } from "@repo/ui";
import { DispenseHistoryTable } from "@/components/dispenses/dispense-history-table";
import { DispenseDetailView } from "@/components/dispenses/dispense-detail-view";
import { DispenseDeleteDialog } from "@/components/dispenses/dispense-delete-dialog";
import { DispenseFormPanel } from "@/components/dispenses/dispense-form-panel";
import type { DispenseCreateContext } from "@/components/dispenses/dispense-form-panel";
import { NotesThread } from "@/components/notes-thread";
import { PermissionGate } from "@/components/security";
import {
  createDispense,
  updateDispense,
  getDispense,
} from "@/lib/api/dispenses";
import { createNote } from "@/lib/api/notes";
import { getPharmacyOptions } from "@/lib/api/pharmacy";
import { showErrorToast, showSuccessToast } from "@/lib/utils/error-display";
import { ApiClientError } from "@/lib/api/client";
import { logger } from "@/lib/utils/logger";
import type { Dispense, DispenseFormData } from "@/lib/types/inventory";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3005";

export const Route = createFileRoute("/_app/dispenses")({
  component: DispensesPage,
});

function DispensesPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const searchStr = useRouterState({ select: (s) => s.location.searchStr });
  const searchParams = new URLSearchParams(searchStr);
  const highlightId = searchParams.get("id");

  const [selectedDispense, setSelectedDispense] = useState<Dispense | null>(
    null
  );
  const [viewMode, setViewMode] = useState<"view" | "notes" | null>(null);
  const [dispenseToDelete, setDispenseToDelete] = useState<Dispense | null>(
    null
  );
  const [dispenseToEdit, setDispenseToEdit] = useState<Dispense | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [patientPrefill, setPatientPrefill] = useState<{
    patientId: string;
    patientName: string;
  } | null>(null);

  // Pharmacy workflow options
  const [confirmStockEnabled, setConfirmStockEnabled] = useState(false);
  const [confirmStockScope, setConfirmStockScope] = useState<
    "cii_only" | "all"
  >("cii_only");
  const [requirePatientEnabled, setRequirePatientEnabled] = useState(false);

  // Fetch pharmacy options on mount
  useEffect(() => {
    getPharmacyOptions()
      .then((res) => {
        setConfirmStockEnabled(res.data.confirm_stock_on_dispense);
        setConfirmStockScope(res.data.confirm_stock_scope);
        setRequirePatientEnabled(res.data.require_patient_on_dispense);
      })
      .catch(() => {
        setConfirmStockEnabled(false);
        setConfirmStockScope("cii_only");
        setRequirePatientEnabled(false);
      });
  }, []);

  // Auto-open create form when navigating with ?action=new&patientId=...&patientName=...
  useEffect(() => {
    const params = new URLSearchParams(searchStr);
    if (params.get("action") === "new") {
      const patientId = params.get("patientId");
      const patientName = params.get("patientName");
      if (patientId && patientName) {
        setPatientPrefill({ patientId, patientName });
      } else {
        setPatientPrefill(null);
      }
      setFormOpen(true);
      navigate({ to: "/dispenses", search: {}, replace: true });
    }
  }, [searchStr, navigate]);

  // Auto-open detail view when navigating with ?id= param
  const didOpenRef = useRef<string | null>(null);
  useEffect(() => {
    if (!highlightId || didOpenRef.current === highlightId) return;
    didOpenRef.current = highlightId;

    getDispense(highlightId)
      .then((result: unknown) => {
        // API returns { dispense: {...} }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const res = result as any;
        const dispense =
          res?.dispense ?? res?.data?.dispense ?? res?.data ?? res;
        if (dispense?.id) {
          setSelectedDispense(dispense as Dispense);
          setViewMode("view");
        }
      })
      .catch(() => {
        navigate({ to: "/dispenses", replace: true });
      });
  }, [highlightId, navigate]);

  const handleCheckDuplicateRx = useCallback(
    async (rxNumber: string): Promise<boolean> => {
      try {
        const response = await fetch(`${API_URL}/dispenses/check-duplicate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ rx_number: rxNumber }),
        });
        const result = await response.json();
        return result.isDuplicate || false;
      } catch (err) {
        logger.error("Check duplicate RX failed", err, {
          operation: "check_duplicate_rx",
        });
        return false;
      }
    },
    []
  );

  const handleCheckStock = useCallback(
    async (ndc: string, quantity: number) => {
      try {
        const response = await fetch(`${API_URL}/dispenses/check-stock`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ ndc, quantity: Number(quantity) }),
        });
        const result = await response.json();

        if (!response.ok) {
          return {
            available: 0,
            willTriggerReorder: false,
            expiringItems: [],
          };
        }

        return result;
      } catch (err) {
        logger.error("Check stock failed", err, {
          operation: "check_stock",
          metadata: { ndc },
        });
        return {
          available: 0,
          willTriggerReorder: false,
          expiringItems: [],
        };
      }
    },
    []
  );

  const handleCreateDispense = useCallback(
    async (
      data: DispenseFormData,
      createContext?: DispenseCreateContext,
      approvalToken?: string
    ) => {
      try {
        const payload = {
          ...(data as Record<string, unknown>),
          ...(approvalToken ? { approvalToken } : {}),
        };
        const result = await createDispense(payload);

        // Create note if provided
        const noteContent = createContext?.note?.trim();
        if (noteContent && result.dispense?.id) {
          try {
            await createNote({
              entityType: "dispense",
              entityId: result.dispense.id,
              content: noteContent,
            });
          } catch (noteError) {
            toast({
              title: "Dispense created, note failed",
              description:
                noteError instanceof ApiClientError
                  ? noteError.message
                  : "Could not save the note. You can add it later.",
              variant: "destructive",
            });
          }
        }

        showSuccessToast(
          `Successfully logged dispense for ${data.item_name}`,
          "Dispense Created"
        );

        if (result.isDuplicate) {
          toast({
            title: "Duplicate RX Number",
            description: result.warning,
          });
        }

        setFormOpen(false);
        setPatientPrefill(null);
        setRefreshTrigger((prev) => prev + 1);
      } catch (error) {
        if (error instanceof ApiClientError && error.statusCode === 403) {
          throw error;
        }
        showErrorToast(error, "Create dispense");
        throw error;
      }
    },
    [toast]
  );

  const handleUpdateDispense = useCallback(
    async (data: DispenseFormData, _ctx?: unknown, approvalToken?: string) => {
      if (!dispenseToEdit) return;
      try {
        const payload = {
          ...(data as Record<string, unknown>),
          ...(approvalToken ? { approvalToken } : {}),
        };
        await updateDispense(dispenseToEdit.id, payload);
        showSuccessToast(
          `Successfully updated dispense for ${data.item_name || data.ndc}`,
          "Dispense Updated"
        );
        setDispenseToEdit(null);
        setRefreshTrigger((prev) => prev + 1);
      } catch (error) {
        if (error instanceof ApiClientError && error.statusCode === 403) {
          throw error;
        }
        showErrorToast(error, "Update dispense");
        throw error;
      }
    },
    [dispenseToEdit]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-foreground">Dispenses</h1>
        <PermissionGate permission="dispenses:create">
          <Button
            size="sm"
            className="gap-1.5 h-9"
            onClick={() => setFormOpen(true)}
            data-testid="dispense-create"
          >
            <ArrowLineUp weight="bold" size={15} />
            Log Dispense
          </Button>
        </PermissionGate>
      </div>

      <DispenseHistoryTable
        onView={(dispense) => {
          setSelectedDispense(dispense);
          setViewMode("view");
        }}
        onEdit={(dispense) => setDispenseToEdit(dispense)}
        onDelete={(dispense) => setDispenseToDelete(dispense)}
        onEditNotes={(dispense) => {
          setSelectedDispense(dispense);
          setViewMode("notes");
        }}
        refreshTrigger={refreshTrigger}
      />

      <DispenseDetailView
        dispense={viewMode === "view" ? selectedDispense : null}
        open={viewMode === "view" && !!selectedDispense}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedDispense(null);
            setViewMode(null);
            if (highlightId) {
              navigate({ to: "/dispenses", replace: true });
            }
          }
        }}
        onEdit={(dispense) => {
          setSelectedDispense(null);
          setViewMode(null);
          setDispenseToEdit(dispense);
        }}
      />

      <NotesThread
        open={viewMode === "notes" && !!selectedDispense}
        onClose={() => {
          setSelectedDispense(null);
          setViewMode(null);
        }}
        entityType="dispense"
        entityId={selectedDispense?.id ?? ""}
        ndc={selectedDispense?.ndc ?? ""}
        itemName={selectedDispense?.inventory?.item_name ?? ""}
      />

      <DispenseDeleteDialog
        dispense={dispenseToDelete}
        open={!!dispenseToDelete}
        onOpenChange={(open) => {
          if (!open) setDispenseToDelete(null);
        }}
        onSuccess={() => {
          setDispenseToDelete(null);
          setRefreshTrigger((prev) => prev + 1);
        }}
      />

      <DispenseFormPanel
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) setPatientPrefill(null);
        }}
        onSubmit={handleCreateDispense}
        onCheckDuplicateRx={handleCheckDuplicateRx}
        onCheckStock={handleCheckStock}
        mode="create"
        initialData={patientPrefill ?? undefined}
        confirmStockEnabled={confirmStockEnabled}
        confirmStockScope={confirmStockScope}
        requirePatientEnabled={requirePatientEnabled}
      />

      <DispenseFormPanel
        open={!!dispenseToEdit}
        onOpenChange={(open) => {
          if (!open) setDispenseToEdit(null);
        }}
        onSubmit={handleUpdateDispense}
        onCheckDuplicateRx={handleCheckDuplicateRx}
        onCheckStock={handleCheckStock}
        mode="edit"
        initialData={
          dispenseToEdit
            ? {
                date: new Date(dispenseToEdit.date + "T00:00:00"),
                time: dispenseToEdit.dispensed_at
                  ? (() => {
                      const d = new Date(dispenseToEdit.dispensed_at);
                      return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
                    })()
                  : undefined,
                ndc: dispenseToEdit.ndc,
                itemName: dispenseToEdit.inventory?.item_name ?? "",
                quantityDispensed: dispenseToEdit.quantity_dispensed,
                rxNumber: dispenseToEdit.rx_number,
                patientId: dispenseToEdit.patient_id ?? undefined,
                patientName: dispenseToEdit.patient_name ?? undefined,
              }
            : undefined
        }
        requirePatientEnabled={requirePatientEnabled}
      />
    </div>
  );
}
